import axios from "axios";
import Swal from "sweetalert2";
import { api_base_url } from "../../environment.jsx";
import { getApiErrorMessage } from "../utils/apiError";

const apiClient = axios.create({
  baseURL: api_base_url,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("dcms_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Remembers the last permission message shown, so parallel reads on one screen don't stack
// identical toasts.
let lastForbidden = { message: "", at: 0 };

// On 401 redirect to login (skip for the login endpoint itself)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes("/api/Auth/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("dcms_token");
      localStorage.removeItem("dcms_user");
      window.location.href = "/signin";
    }

    // A 403 on a READ has nothing to report it: the screen just renders empty, which reads as
    // "there is no data" rather than "your role may not see this". Writes are left alone here —
    // their own modal already shows the message in context, and a toast would duplicate it.
    // RequirePermissionAttribute names the missing permission, so show what the API said.
    if (error.response?.status === 403 && (error.config?.method || "get").toLowerCase() === "get") {
      const msg = getApiErrorMessage(error, "You do not have permission to view this.");
      const now = Date.now();
      // One screen can fire several reads at once and get a 403 for each; collapse the repeats.
      if (msg !== lastForbidden.message || now - lastForbidden.at > 3000) {
        lastForbidden = { message: msg, at: now };
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: msg,
          showConfirmButton: false,
          timer: 4000,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
